import { Country } from "../types";
import { IpAddressResolverService } from "./ipAddressResolverService";

export interface CountryDetectionClient {
  resolveIpToCountry(ipAddress: string): Promise<Country | null>;
  detectCountry(): Promise<Country | null>;
  resolveCountryByCoordinates(
    latitude: number,
    longitude: number,
  ): Promise<Country | null>;
  findCountryByCode(code: string): Promise<Country | null>;
}

export interface ClientIpResolver {
  resolveClientIpAddress(): Promise<string>;
}

const browserGeolocation = (): Geolocation | null =>
  typeof navigator === "undefined" ? null : navigator.geolocation || null;

const browserLanguages = (): readonly string[] => {
  if (typeof navigator === "undefined") {
    return [];
  }
  if (navigator.languages && navigator.languages.length > 0) {
    return navigator.languages;
  }
  return navigator.language ? [navigator.language] : [];
};

/**
 * Extracts distinct uppercase ISO-3166 alpha-2 region codes from BCP-47 language
 * tags (e.g. en-GB -> GB, zh-Hant-TW -> TW). Numeric UN M.49 regions (e.g. es-419)
 * have no alpha-2 equivalent and are skipped.
 */
const regionCodesFromLanguages = (languages: readonly string[]): string[] => {
  const regions: string[] = [];
  for (const language of languages) {
    const region = language
      .split(/[-_]/)
      .slice(1)
      .find((part) => /^[A-Za-z]{2}$/.test(part));
    if (region) {
      const normalized = region.toUpperCase();
      if (!regions.includes(normalized)) {
        regions.push(normalized);
      }
    }
  }
  return regions;
};

/**
 * Applies the country resolution chain used during application startup.
 * Each provider is optional; a provider failure must never prevent the UI
 * from loading.
 *
 * Ordered most-reliable first:
 *   1. Server request metadata  -> /country/detect
 *      (CDN CF-IPCountry header first, then server-side GeoIP — edge-geolocated,
 *      so it is correct for mobile/IPv6 and is preferred over a raw IP lookup)
 *   2. Client public IP (ipify)  -> /ip lookup
 *   3. Browser geolocation coordinates -> /country/coordinates
 *   4. Browser language/region   -> /country/code/{region} (e.g. en-GB -> GB)
 *   5. Ask the visitor to choose an active country in the UI
 */
export class CountryResolutionService {
  private resolution?: Promise<Country | null>;

  constructor(
    private readonly locationClient: CountryDetectionClient,
    private readonly geolocation: Geolocation | null = browserGeolocation(),
    private readonly ipResolver: ClientIpResolver | null = new IpAddressResolverService(),
    private readonly languageProvider: () => readonly string[] = browserLanguages,
  ) {}

  resolveCountry(): Promise<Country | null> {
    this.resolution ??= this.resolveCountryOnce();
    return this.resolution;
  }

  private async resolveCountryOnce(): Promise<Country | null> {
    // 1. Server request metadata: CDN CF-IPCountry (edge-geolocated, most
    //    reliable) then server-side GeoIP. Preferred over the raw IP lookup.
    const requestCountry = await this.resolveSafely(
      () => this.locationClient.detectCountry(),
      "request metadata",
    );
    if (requestCountry) {
      return requestCountry;
    }

    // 2. Client's own public IP -> GeoIP lookup.
    const clientIpCountry = await this.resolveFromClientIp();
    if (clientIpCountry) {
      return clientIpCountry;
    }

    // 3. Browser geolocation coordinates.
    const coordinateCountry = await this.resolveFromBrowserCoordinates();
    if (coordinateCountry) {
      return coordinateCountry;
    }

    // 4. Last automatic signal: infer from the browser's language/region.
    //    Returning null is intentional: the UI asks the visitor before it uses
    //    the configured US fallback.
    const languageCountry = await this.resolveFromBrowserLanguage();
    return languageCountry;
  }

  private async resolveFromClientIp(): Promise<Country | null> {
    if (!this.ipResolver) {
      return null;
    }

    return this.resolveSafely(async () => {
      const ipAddress = await this.ipResolver!.resolveClientIpAddress();
      if (!ipAddress) {
        return null;
      }
      return this.locationClient.resolveIpToCountry(ipAddress);
    }, "client IP");
  }

  private async resolveFromBrowserCoordinates(): Promise<Country | null> {
    if (!this.geolocation) {
      return null;
    }

    let coordinates: GeolocationCoordinates | null;
    try {
      coordinates = await new Promise<GeolocationCoordinates | null>(
        (resolve) => {
          this.geolocation?.getCurrentPosition(
            (position) => resolve(position.coords),
            () => resolve(null),
            { enableHighAccuracy: false, maximumAge: 300_000, timeout: 5000 },
          );
        },
      );
    } catch (error: unknown) {
      console.warn("Browser geolocation failed", error);
      return null;
    }

    if (!coordinates) {
      return null;
    }
    const { latitude, longitude } = coordinates;

    return this.resolveSafely(
      () =>
        this.locationClient.resolveCountryByCoordinates(
          latitude,
          longitude,
        ),
      "browser coordinates",
    );
  }

  private async resolveFromBrowserLanguage(): Promise<Country | null> {
    for (const code of regionCodesFromLanguages(this.languageProvider())) {
      const country = await this.resolveSafely(
        () => this.locationClient.findCountryByCode(code),
        `browser language (${code})`,
      );
      if (country) {
        return country;
      }
    }
    return null;
  }

  private async resolveSafely(
    resolver: () => Promise<Country | null>,
    source: string,
  ): Promise<Country | null> {
    try {
      return await resolver();
    } catch (error: unknown) {
      console.warn(`Country resolution from ${source} failed`, error);
      return null;
    }
  }
}
